import { zodResolver } from "@hookform/resolvers/zod";
import {
  useCreateClient,
  useCreateInvestmentAccount,
  useDistributorProfile,
} from "creso-core/hooks";
import { CreateClientSchema } from "creso-core/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  Button,
  cn,
} from "web-design-system";
import { CopyIcon, InfoIcon, Loader2, Loader2Icon } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { handleError, handleFormValidationErrors } from "@/utils/error";
import { useAuthContext } from "@/providers/auth-provider";
import { getInitials, isEmployeeUser } from "creso-core/utils";
import { useCopyToClipboard } from "usehooks-ts";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Avatar, ClientStatusBadge } from "web-core";
import { Client } from "creso-core/types";
import { useCheckKyc } from "@crm/hooks/onboarding";
import { KycDescription, KycStatusBadge } from "./kyc-status";

export function ClientLeadAccountForm(props: { onClose?: () => void }) {
  const { onClose } = props;
  const { user, employee } = useAuthContext();
  const { data: profile } = useDistributorProfile();
  const isEmployee = isEmployeeUser(user.user_type);
  const onboardingUrl = `${window.location.origin}/mfd/${profile?.handle}${isEmployee && employee?.id ? `?employee_id=${employee?.id}` : ""}`;

  const [, copy] = useCopyToClipboard();
  const [client, setClient] = useState<Client | null>(null);
  const [cachedClientName, setCachedClientName] = useState<string | null>(null);

  const [checkKyc, isCheckingKyc] = useCheckKyc(client?.id ?? 0);

  const form = useForm({
    resolver: zodResolver(CreateClientSchema),
    values: {
      holding_pattern: "single",
      pan: client?.pan || "",
      phone_number: client?.phone_number || "",
      name: client?.name || "",
      email: client?.email,
    },
  });

  const { isSubmitting, dirtyFields } = form.formState;
  const panIsDirty = !!dirtyFields.pan;
  const panValue = form.watch("pan");

  const createClient = useCreateClient();
  const navigate = useNavigate();
  const createInvestmentAccount = useCreateInvestmentAccount();

  const addEditClient = async (data: z.infer<typeof CreateClientSchema>) => {
    try {
      const client = await createClient.mutateAsync(data);
      setClient(client);
    } catch (error) {
      handleFormValidationErrors(form, error);
      handleError(error, "Failed to create client");
    }
  };

  const handleCheckKyc = useCallback(
    async (data: z.infer<typeof CreateClientSchema>) => {
      setCachedClientName(client?.name || null);
      try {
        const client = await checkKyc(data);
        setClient(client);
      } catch (e) {
        handleError(e, "Failed to check KYC status");
      }
    },
    [client, checkKyc]
  );

  const createAccount = useCallback(async () => {
    if (!client?.id) return;
    try {
      await createInvestmentAccount.mutateAsync(client.id);
      toast.success("Client account created");
      onClose?.();
      navigate({
        to: "/clients/$id/portfolio",
        params: {
          id: client.id.toString(),
        },
        search: {},
      });
    } catch (error) {
      handleError(error, "Failed to send consent link");
    }
  }, [client, createInvestmentAccount, navigate, onClose]);

  const clientCreated = !!client;
  const showCheckKycButton =
    !client?.kyc_status ||
    client?.kyc_status === "U" ||
    !panValue ||
    panIsDirty;
  const showKycStatus = !showCheckKycButton;
  const nameChanged =
    showKycStatus &&
    !!cachedClientName &&
    !!client?.name &&
    cachedClientName !== client.name;

  return (
    <div className="flex flex-col size-full p-6">
      <span className="text-2xl font-semibold mb-6">
        {clientCreated ? "Create  Account" : "New Client"}
      </span>
      <Form {...form}>
        <form className="flex flex-col flex-1 gap-6">
          <ConditionalRenderer condition={!clientCreated}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormInput
                  {...field}
                  label="Name"
                  placeholder="Name"
                  value={field.value || ""}
                />
              )}
            />
            <FormField
              control={form.control}
              name="phone_number"
              render={({ field }) => (
                <FormPrefixedInput
                  {...field}
                  prefix="+91"
                  label="Mobile"
                  value={field.value || ""}
                  placeholder="9999999999"
                >
                  +91
                </FormPrefixedInput>
              )}
            />
            <FormField
              name="email"
              render={({ field }) => (
                <FormInput
                  label={
                    <>
                      <span>Email</span>&nbsp;
                      <span className="text-muted-foreground">(Optional)</span>
                    </>
                  }
                  {...field}
                  placeholder="Enter email address"
                />
              )}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={clientCreated}>
            <div className="rounded-lg p-3 border border-border flex items-center gap-2">
              <Avatar fallback={getInitials(client?.name || "")} size="sm" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold">{client?.name}</span>
                {client?.phone_number && (
                  <span className="text-xs text-muted-foreground">
                    {client.phone_number}
                  </span>
                )}
              </div>
              <ClientStatusBadge status={client?.status ?? "LEAD"} />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-row items-start justify-between gap-2">
                <FormField
                  control={form.control}
                  name="pan"
                  className="flex-2"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      disabled={isSubmitting}
                      label="PAN"
                      placeholder="ABCDE1234J"
                      onChange={(val) =>
                        field.onChange(val?.toLocaleUpperCase() || "")
                      }
                    />
                  )}
                />
                <div className="shrink-0 pt-5">
                  {showCheckKycButton ? (
                    <Button
                      variant="outline"
                      disabled={isCheckingKyc || !panValue}
                      type="button"
                      onClick={form.handleSubmit(handleCheckKyc)}
                    >
                      <Loader2Icon
                        className={cn("size-4 animate-spin", {
                          hidden: !isCheckingKyc,
                        })}
                      />
                      Check KYC
                    </Button>
                  ) : (
                    <KycStatusBadge
                      kyc_status={client?.kyc_status}
                      remark={client?.kyc_status_remark}
                    />
                  )}
                </div>
              </div>
              {showKycStatus && (
                <KycDescription
                  client={client}
                  cachedName={nameChanged ? cachedClientName : undefined}
                />
              )}
            </div>
          </ConditionalRenderer>
          <DialogFooter className="flex sm:flex-col gap-2 mt-auto">
            <ConditionalRenderer condition={clientCreated}>
              {showCheckKycButton && (
                <div className="flex items-center gap-2 rounded-lg bg-tag-slate p-3 border border-border w-full">
                  <InfoIcon className="size-4 shrink-0 text-tag-slate-foreground" />
                  <span className="text-xs text-tag-slate-foreground">
                    Complete KYC check to create the account
                  </span>
                </div>
              )}
              <Button
                className="w-full"
                disabled={
                  createInvestmentAccount.isPending ||
                  showCheckKycButton ||
                  isSubmitting
                }
                type="button"
                onClick={form.handleSubmit(createAccount)}
              >
                Create Account
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setClient(null);
                  setCachedClientName(null);
                }}
                variant="ghost"
              >
                Add Another Lead
              </Button>
            </ConditionalRenderer>
            <ConditionalRenderer condition={!clientCreated}>
              <Button
                className="w-full"
                type="button"
                onClick={form.handleSubmit(addEditClient)}
                disabled={createClient.isPending}
              >
                {createClient.isPending && (
                  <Loader2 className="ml-2 size-4 animate-spin" />
                )}
                Add
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  copy(onboardingUrl);
                  toast.info("Onboarding link copied to clipboard");
                }}
                variant="ghost"
              >
                Or share onboarding link <CopyIcon />{" "}
              </Button>
            </ConditionalRenderer>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
